import { useEffect, useMemo, useRef } from 'react';
import classNames from 'classnames';

import {
  FlyoutContainer,
  FlyoutPanel,
} from 'components/shared/side-menu/flyout-container';
import { useHoverFlyout } from 'components/shared/side-menu/hooks';
import { openSideMenuBadgePopover } from 'components/shared/side-menu/side-menu-badge-popover';
import { SIDE_MENU_POPOVER_GAP } from 'components/shared/side-menu/side-menu.constants';
import { SIDE_MENU_POPOVER_ANCHOR_ATTRIBUTE } from 'components/shared/side-menu/side-menu-popover-events';

import type { NavItem } from 'components/shared/side-menu/nav-config';

import BaseSideMenuItem from './base-side-menu-item';

interface BaseCollapsedSectionWithIconProps {
  title?: string;
  sectionIcon: React.ReactNode;
  visibleItems: NavItem[];
  isSectionActive: boolean;
  isItemActive: (item: NavItem) => boolean;
  renderLink: (props: Record<string, unknown>) => React.ReactNode;
  getNotification?: (item: NavItem) => boolean;
  className?: string;
}

const BaseCollapsedSectionWithIcon = ({
  title,
  sectionIcon,
  visibleItems,
  isSectionActive,
  isItemActive,
  renderLink,
  getNotification,
  className,
}: BaseCollapsedSectionWithIconProps) => {
  const { isHovered, flyoutTop, hoverProps, closeFlyout } = useHoverFlyout();
  const flyoutRef = useRef<HTMLDivElement>(null);
  const flyoutPopoverItems = useMemo(
    () => visibleItems.filter((item) => item.badge?.popover),
    [visibleItems]
  );

  useEffect(() => {
    if (!isHovered || !flyoutPopoverItems.length) return;

    flyoutPopoverItems.forEach((item) => {
      const popover = item.badge?.popover;

      if (!popover) return;

      const flyoutAnchorElement =
        flyoutRef.current?.querySelector<HTMLElement>(
          `[${SIDE_MENU_POPOVER_ANCHOR_ATTRIBUTE}="${popover}"]`
        ) ?? flyoutRef.current;

      openSideMenuBadgePopover(popover, {
        anchorElement: flyoutAnchorElement,
        repositionOnly: true,
        anchorGap: SIDE_MENU_POPOVER_GAP.row,
      });
    });
  }, [flyoutPopoverItems, isHovered]);

  return (
    <div {...hoverProps} className={classNames('py-1 relative', className)}>
      <div className="flex justify-center px-2">
        <button
          className={classNames(
            'w-10 h-10 rounded-lg flex items-center justify-center transition-colors',
            {
              'bg-gray-100 text-gray-700': isSectionActive,
              'text-gray-700 hover:bg-gray-100': !isSectionActive,
            }
          )}
          title={title}
        >
          {sectionIcon}
        </button>
      </div>

      <FlyoutContainer
        isVisible={isHovered}
        top={flyoutTop}
        containerRef={flyoutRef}
      >
        <FlyoutPanel className="py-2 min-w-[200px]">
          {title && (
            <div className="px-4 py-2 text-gray-400 font-semibold text-sm">
              {title}
            </div>
          )}
          <nav className="space-y-0.5 px-2">
            {visibleItems.map((item) => (
              <BaseSideMenuItem
                key={item.id}
                item={item}
                isCollapsed={false}
                inFlyout
                isItemActive={isItemActive}
                renderLink={renderLink}
                hasNotification={getNotification?.(item) ?? false}
                onNavigate={closeFlyout}
              />
            ))}
          </nav>
        </FlyoutPanel>
      </FlyoutContainer>
    </div>
  );
};

export default BaseCollapsedSectionWithIcon;
